/**
 * The stage vocabulary, shared by the server's decision machine and the client's action bar.
 * Both read the same TRANSITIONS table, so the client can grey out a button the server would
 * refuse instead of discovering the refusal after the click.
 *
 * `applications.stage` means *where a human has moved this candidate*, full stop. Machine
 * progress lives in `applications.screening_state` and the counters and is never a stage —
 * conflating the two is exactly the confusion that produced "viewing an applicant flips
 * their status".
 */
export const STAGES = [
    'submitted', 'under_review', 'shortlisted', 'interviewing', 'hired', 'rejected', 'withdrawn',
];
export const TERMINAL_STAGES = ['hired', 'rejected', 'withdrawn'];
export const TERMINAL = new Set(TERMINAL_STAGES);
export const DECISION_ACTIONS = [
    'start_review', 'shortlist', 'advance_to_interview', 'hire', 'reject',
    'record_withdrawal', 'reopen',
];
export const REASON_CODES = [
    'strong_work', 'weak_work', 'insufficient_evidence', 'role_requirements_unmet',
    'stronger_candidates', 'role_filled', 'role_cancelled', 'candidate_withdrew',
    'offer_declined', 'integrity_concern', 'other',
];
/**
 * FOUNDER RULING 5 — the withdraw button ships (POST /api/applications/:id/withdraw), so every
 * `-> withdrawn` edge accepts actorKind 'candidate' as well as 'recruiter' (a recruiter records
 * a withdrawal the candidate told them about out of band). This constant is the ONLY place that
 * list exists.
 *
 * `requiresOwner: false` is the half that makes the button implementable rather than decorative.
 * A job seeker holds no company_members row, so there is no company role to be an owner of; an
 * owner-gated withdraw edge would make the candidate's own withdrawal impossible. The other
 * side of the coin — a member recruiter may also record one — is deliberate: recording that a
 * candidate walked away is not a hiring decision, and forcing an owner to do it just means it
 * does not get recorded at all.
 */
const WITHDRAW = {
    actorKinds: ['candidate', 'recruiter'], requiresOwner: false, requiresRationale: false,
};
/**
 * Every recruiter edge requires an owner: a member views candidates and adds notes, and does not
 * decide.
 *
 * `requiresRationale` is NOT a mirror of decisions_rationale_check, and NEITHER RULE DOMINATES
 * THE OTHER — which is exactly why server/src/review/decide.ts enforces their UNION rather than
 * picking one:
 *
 *   - This flag is stricter on the two reopen edges (`rejected -> under_review`,
 *     `hired -> under_review`). Both are marked true here; the CHECK would accept an empty
 *     rationale on either, because to_stage is not hired/rejected.
 *   - The CHECK is stricter on every edge marked false here whenever reason_code is 'other' —
 *     `submitted -> under_review` with reason_code 'other' and a blank rationale is refused by
 *     the CHECK's condition and permitted by this flag.
 *
 * So a reopen cannot be recorded without saying why, and neither can an 'other'-coded decision.
 */
const RECRUITER = (requiresRationale) => ({ actorKinds: ['recruiter'], requiresOwner: true, requiresRationale });
export const TRANSITIONS = {
    submitted: {
        under_review: RECRUITER(false), shortlisted: RECRUITER(false),
        rejected: RECRUITER(true), withdrawn: WITHDRAW,
    },
    under_review: {
        shortlisted: RECRUITER(false), interviewing: RECRUITER(false),
        rejected: RECRUITER(true), withdrawn: WITHDRAW,
    },
    shortlisted: {
        interviewing: RECRUITER(false), rejected: RECRUITER(true), withdrawn: WITHDRAW,
    },
    interviewing: {
        hired: RECRUITER(true), shortlisted: RECRUITER(false), // shortlisted = a deliberate step back
        rejected: RECRUITER(true), withdrawn: WITHDRAW,
    },
    // Terminal for a recruiter. Only an admin reopens a hire, and only with a written reason.
    hired: { under_review: { actorKinds: ['admin'], requiresOwner: true, requiresRationale: true } },
    // Reopening a rejection is legal and always needs a written reason.
    rejected: { under_review: RECRUITER(true) },
    // Terminal, full stop. A withdrawal is the candidate's to make and the candidate's to keep.
    withdrawn: {},
};
export const CANDIDATE_STAGES = [
    'received', 'in_review', 'interviewing', 'hired', 'closed', 'withdrawn',
];
/**
 * Deliberately a pure function and NOT a STORED generated column: candidate-facing copy is
 * exactly the thing founders iterate on, and Postgres cannot ALTER a generation expression —
 * a wording change would become a full table rewrite under ACCESS EXCLUSIVE. The stored value
 * is a stable token; display copy lives in the client.
 *
 * The same mapping is applied a second time, in SQL, inside `candidate_application_view`, which
 * is what the seeker's own list reads. That is not duplication for its own sake: a TypeScript
 * map can be bypassed by the next route that selects `applications.stage` directly, and a view
 * cannot.
 */
export function candidateStage(stage) {
    switch (stage) {
        case 'submitted': return 'received';
        case 'under_review': return 'in_review';
        // `shortlisted` collapses into in_review on purpose: it is the AI ranking's outcome, and
        // surfacing it leaks the pre-screen result and creates an implied promise before any human
        // has spoken to the candidate. Telling someone they are shortlisted and then rejecting
        // them is worse than silence.
        case 'shortlisted': return 'in_review';
        case 'interviewing': return 'interviewing';
        case 'hired': return 'hired';
        // Every rejection collapses to `closed` regardless of exit stage, so a candidate never
        // learns they reached interviewing and were cut. exited_from_stage keeps that internally.
        case 'rejected': return 'closed';
        case 'withdrawn': return 'withdrawn';
        default: {
            const unmapped = stage;
            throw new Error(`unmapped stage: ${String(unmapped)}`);
        }
    }
}
export function stageNotifiesCandidate(stage) {
    switch (stage) {
        // FOUNDER RULING 1 — `interviewing` does NOT notify the candidate. The recruiter arranges
        // the interview themselves; an automated "you are interviewing" mail either duplicates
        // that or arrives before it and promises a slot nobody has booked. This is the ONE place
        // that decision is encoded — do not add a second notification policy anywhere else.
        case 'interviewing': return false;
        // Nothing to tell: the candidate sees in_review either way (see candidateStage).
        case 'shortlisted': return false;
        case 'hired': return true;
        case 'rejected': return true;
        case 'submitted': return false; // the apply response is the confirmation
        case 'under_review': return false;
        case 'withdrawn': return false; // they did it
        default: {
            const unmapped = stage;
            throw new Error(`unmapped stage: ${String(unmapped)}`);
        }
    }
}
